// Add/remove string tags on Account/Contact/Lead records.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface Props {
  endpoint: string;                 // e.g. "/accounts/abc-123"
  queryKey: unknown[];              // parent detail query key to invalidate
  tags: string[];
}

export function TagsPanel({ endpoint, queryKey, tags }: Props) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");

  const mut = useMutation({
    mutationFn: async (next: string[]) => {
      await api.put(endpoint, { tags: next });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  function add() {
    const t = input.trim();
    if (!t || tags.includes(t)) { setInput(""); return; }
    mut.mutate([...tags, t]);
    setInput("");
  }

  function remove(t: string) {
    mut.mutate(tags.filter((x) => x !== t));
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {tags.length === 0 && <p className="text-sm text-muted-foreground">No tags yet.</p>}
        {tags.map((t) => (
          <Badge key={t} variant="secondary" className="flex items-center gap-1 pr-1">
            {t}
            <button
              onClick={() => remove(t)}
              className="flex h-4 w-4 items-center justify-center rounded-sm hover:bg-muted-foreground/20"
              aria-label={`Remove ${t}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Add tag…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="h-8 max-w-xs"
        />
        <Button size="sm" onClick={add} disabled={!input.trim() || mut.isPending}>Add</Button>
      </div>
    </div>
  );
}
